import { FileJson, Globe, Loader2, RefreshCw } from "lucide-react";
import { useState } from "react";
import { cn } from "@wa/ui";
import { Btn } from "../../../ui";
// The register's domain check, reused as-is. It answers for the same
// registrable domain the scan is keyed on, so joining the two here needs no new
// endpoint and no second cache - only the card around it is new.
import { DomainRawDialog, DomainSummary, useDomainLookup } from "../../ariregister/domain";
import { Panel } from "./primitives";
import type { T } from "../types";

/**
 * Domain and DNS for the scanned site.
 *
 * The scan says what the site is built with; this says who it is built ON - the
 * registration date that separates a fifteen-year-old business from a two-month
 * one, and the two provider names that decide who has to be talked out of the
 * job. Both halves of that conversation now live on one page.
 *
 * It runs by itself: the operator typed one address and pressed scan, so one
 * more lookup for that same address is what they asked for. The registry answer
 * is cached for a week server-side, which is why re-opening a stored scan costs
 * nothing.
 */
export function DomainPanel({ domain, t }: { domain: string; t: T }) {
  const { state, run } = useDomainLookup(domain, true);
  const [rawOpen, setRawOpen] = useState(false);

  return (
    <Panel
      icon={Globe}
      title={t("ariregister.dns.title")}
      aside={
        state.kind === "done" ? (
          <>
            <Btn small onClick={() => setRawOpen(true)}>
              <FileJson className="size-3.5" /> {t("ariregister.dns.raw")}
            </Btn>
            <Btn small onClick={() => void run(true)}>
              <RefreshCw className="size-3.5" /> {t("ariregister.dns.recheck")}
            </Btn>
          </>
        ) : (
          <Btn small onClick={() => void run(false)} disabled={state.kind === "busy"}>
            <RefreshCw className={cn("size-3.5", state.kind === "busy" && "animate-spin")} />
            {t(state.kind === "busy" ? "ariregister.dns.checking" : "ariregister.dns.check")}
          </Btn>
        )
      }
    >
      {state.kind === "busy" && (
        <p className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
          <Loader2 className="size-4 animate-spin" /> {t("ariregister.dns.checking")}
        </p>
      )}

      {state.kind === "idle" && <p className="text-sm text-muted-foreground">{t("ariregister.dns.hint")}</p>}

      {state.kind === "error" && (
        <p className="crm-alert px-4 py-2.5 text-xs">{t(state.messageKey)}</p>
      )}

      {state.kind === "done" && (
        <>
          <DomainSummary check={state.check} t={t} />
          <DomainRawDialog check={state.check} open={rawOpen} onOpenChange={setRawOpen} t={t} />
        </>
      )}
    </Panel>
  );
}
